import React, { useState } from "react";
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Edit, Trash2, Eye } from "lucide-react";
import { alpha } from "@mui/material";

import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useNavigate, useParams } from "react-router-dom";
import { useDestroy } from "../../../../hooks/useDestroy";
import { useDispatch, useSelector } from "react-redux";
import { setIsEditMode } from "../../../../redux/productItem/productItemSlice";
import formatDate from "../../../../Utils/formatDate";
import { setRefresh } from "../../../../redux/refreshSlice";

const ProductItemRow = ({ row, index }) => {
  const { id } = useParams();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { destroyData, loading } = useDestroy(`/medicine-item`);

  const { editProductItem } = useSelector((state) => state.productItem);
  const { refresh } = useSelector((state) => state.refresh);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    await destroyData(row._id);
    dispatch(setRefresh(!refresh));
    setDeleteModalOpen(false);
  };

  const handleEditBtn = () => {
    dispatch(
      setIsEditMode({
        data: row,
        isEditMode: !editProductItem.isEditMode,
      }),
    );
  };

  const handleUnitBtn = () => {
    navigate(`/product-item/unit/${row._id}?item=${row?.medicineItemName}`);
  };

  return (
    <>
      <TableRow
        key={row._id}
        sx={{
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {index + 1}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={500}>
            {row.medicineItemName}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.code || "-"}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.barcode || "-"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.description}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography
            variant="body2"
            fontWeight={500}
            onClick={handleUnitBtn}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              cursor: "pointer",
            }}
          >
            Unit
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(row.createdAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title={loading ? "Deleting..." : "Delete"}>
              <IconButton
                size="small"
                color="error"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Trash2 size={16} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={handleEditBtn}>
                <Edit size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        item={row}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />
    </>
  );
};

export default ProductItemRow;
